import React, { useMemo, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';

const NODE_WIDTH = 200;
const NODE_HEIGHT = 60;

const phaseColors = {
  Succeeded: '#00b894',
  Running: '#0984e3',
  Failed: '#d63031',
  Error: '#d63031',
  Pending: '#636e72',
  Skipped: '#b2bec3',
};

function getLayoutedElements(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 50, ranksep: 80 });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = g.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

export function PipelineDAG({ workflowNodes, onNodeClick }) {
  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    if (!workflowNodes || workflowNodes.length === 0) {
      return { nodes: [], edges: [] };
    }

    const rfNodes = workflowNodes.map((node, i) => ({
      id: `node-${i}`,
      data: {
        label: (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: 12,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {node.name}
            </div>
            <div style={{
              fontSize: 11,
              color: phaseColors[node.phase] || '#636e72',
              fontWeight: 500,
              marginTop: 2,
            }}>
              {node.phase}
            </div>
          </div>
        ),
      },
      style: {
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        background: '#fff',
        border: `2px solid ${phaseColors[node.phase] || '#dfe6e9'}`,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      },
    }));

    // Connect nodes sequentially (simple pipeline)
    const rfEdges = [];
    for (let i = 0; i < rfNodes.length - 1; i++) {
      rfEdges.push({
        id: `edge-${i}`,
        source: rfNodes[i].id,
        target: rfNodes[i + 1].id,
        animated: workflowNodes[i].phase === 'Running',
        style: { stroke: phaseColors[workflowNodes[i].phase] || '#dfe6e9' },
      });
    }

    return getLayoutedElements(rfNodes, rfEdges);
  }, [workflowNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  useEffect(() => { setNodes(layoutedNodes); }, [layoutedNodes, setNodes]);
  useEffect(() => { setEdges(layoutedEdges); }, [layoutedEdges, setEdges]);

  const handleNodeClick = useCallback((_, node) => {
    const index = parseInt(node.id.replace('node-', ''), 10);
    if (onNodeClick && workflowNodes[index]) {
      onNodeClick(workflowNodes[index]);
    }
  }, [onNodeClick, workflowNodes]);

  if (!workflowNodes || workflowNodes.length === 0) {
    return <p style={{ color: '#636e72' }}>No workflow nodes to display.</p>;
  }

  return (
    <div style={{ height: Math.max(300, workflowNodes.length * 100 + 100), border: '1px solid #dfe6e9', borderRadius: 8 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} />
        <Controls />
      </ReactFlow>
    </div>
  );
}
